import type {
  ConversationDTO,
  ConversationDetailDTO,
} from '@eriko/shared-types';
import { apiClient } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { ENDPOINTS } from './endpoints';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export async function getOrgConversations(orgId: string): Promise<ConversationDTO[]> {
  const res = await apiClient.get<ConversationDTO[]>(ENDPOINTS.conversations.list, {
    params: { orgId },
  });
  return res.data;
}

export async function getOrgConversationDetail(
  orgId: string,
  conversationId: string,
): Promise<ConversationDetailDTO> {
  const res = await apiClient.get<ConversationDetailDTO>(
    ENDPOINTS.conversations.detail(conversationId),
    { params: { orgId } },
  );
  return res.data;
}

export async function getReviewerAIConversations(orgId: string): Promise<ConversationDTO[]> {
  const res = await apiClient.get<ConversationDTO[]>(ENDPOINTS.conversations.list, {
    params: { orgId, threadType: 'REVIEWER_AI' },
  });
  return res.data;
}

export async function resolveConversation(orgId: string, conversationId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.conversations.resolve(conversationId), null, {
    params: { orgId },
  });
}

export async function archiveConversation(orgId: string, conversationId: string): Promise<void> {
  await apiClient.post(ENDPOINTS.conversations.archive(conversationId), null, {
    params: { orgId },
  });
}

export async function sendReply(
  orgId: string,
  conversationId: string,
  payload: { content: string; triggerAI: boolean; internalNote?: boolean },
): Promise<void> {
  await apiClient.post(ENDPOINTS.conversations.messages(conversationId), payload, {
    params: { orgId },
  });
}

export async function draftReply(
  orgId: string,
  conversationId: string,
): Promise<{ draft: string }> {
  const res = await apiClient.post<{ draft: string }>(
    ENDPOINTS.conversations.draftReply(conversationId),
    {},
    { params: { orgId } },
  );
  return res.data;
}

/**
 * Create a new conversation and stream the AI response via SSE.
 * Returns the raw Response for ReadableStream parsing.
 */
export async function createConversation(
  orgId: string,
  initialMessage: string,
  threadType: string = 'REVIEWER_AI',
): Promise<ConversationDTO> {
  const res = await apiClient.post<ConversationDTO>(
    ENDPOINTS.conversations.list,
    { initialMessage, threadType },
    { params: { orgId } },
  );
  return res.data;
}

/**
 * Send a message and stream the AI response via SSE (raw fetch for ReadableStream).
 */
export function sendMessageStream(
  orgId: string,
  conversationId: string,
  body: {
    content: string;
    triggerAI: boolean;
    attachments?: Array<{
      fileKey: string;
      fileName: string;
      fileType: string;
      fileUrl: string;
    }>;
  },
): Promise<Response> {
  const token = useAuthStore.getState().accessToken;
  return fetch(
    `${BASE_URL}${ENDPOINTS.conversations.messages(conversationId)}?orgId=${orgId}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    },
  );
}
